<template>
  <div class="grid gap-3 lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-1 mt-4 pt-2">
    <div class="bg-white p-3 rounded-lg shadow flex flex-col">
      <div class="text-sm text-gray-500">Dinero Pendiente</div>
      <div class="text-xl font-bold mt-1">{{ formatCurrency(dineroPendiente) }}</div>
      <div class="text-xs text-gray-400 mt-1">Total pendiente combinado de todas las empresas</div>
    </div>

    <div class="bg-white p-3 rounded-lg shadow flex flex-col">
      <div class="text-sm text-gray-500">Empresas</div>
      <div class="text-xl font-bold mt-1">{{ empresasCount }}</div>
      <div class="text-xs text-gray-400 mt-1">Cantidad de tenants registrados</div>
    </div>

    <div class="bg-white p-3 rounded-lg shadow flex flex-col">
      <div class="text-sm text-gray-500">Usuarios</div>
      <div class="text-xl font-bold mt-1">{{ usuariosCount }}</div>
      <div class="text-xs text-gray-400 mt-1">Usuarios totales en la plataforma</div>
    </div>

    <div class="bg-white p-3 rounded-lg shadow flex flex-col">
      <div class="text-sm text-gray-500">Usuarios desactivados</div>
      <div class="text-xl font-bold mt-1 text-rose-600">{{ usuariosDesactivados }}</div>
      <div class="text-xs text-gray-400 mt-1">Usuarios con acceso deshabilitado</div>
    </div>

    <div class="bg-white p-3 rounded-lg shadow flex flex-col">
      <div class="text-sm text-gray-500">Pendientes (últimos 30 días)</div>
      <div class="text-xl font-bold mt-1">{{ formatCurrency(ultimoMesPendiente) }}</div>
      <div class="text-xs text-gray-400 mt-1">Tendencia en los últimos 30 días</div>
    </div>

    <div class="col-span-4 grid lg:grid-cols-2 gap-4 mt-4">
      <div class="bg-white p-3 rounded-lg shadow">
        <div class="flex items-center justify-between">
          <h3 class="text-base font-medium mb-2">Últimos usuarios conectados</h3>
          <button @click="loadMetrics" :disabled="loading" :aria-busy="loading" title="Refrescar métricas" aria-label="Refrescar métricas" class="ml-3 inline-flex items-center justify-center p-1.5 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-60 disabled:cursor-not-allowed">
            <template v-if="loading">
              <svg class="w-4 h-4 animate-spin text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
            </template>
            <template v-else>
              <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582M20 20v-5h-.581M5.05 19A9 9 0 1119 5.05M4 4l5 5" />
              </svg>
            </template>
          </button>
        </div>
        <div class="overflow-x-auto overflow-y-auto max-h-72">
          <table class="w-full text-xs text-left text-gray-600 table-auto compact-table">
            <thead class="text-xs text-gray-500 uppercase bg-gray-50">
              <tr>
                <th class="px-2 py-1">Nombre</th>
                <th class="px-2 py-1">Email</th>
                <th class="px-2 py-1">Última conexión</th>
                <th class="px-2 py-1">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in ultimosUsuarios" :key="u.id || u._id || u.email" class="border-b">
                <td class="px-2 py-1">{{ u.nombre || u.name || u.fullName || u.username || u.email }}</td>
                <td class="px-2 py-1">{{ u.email }}</td>
                <td class="px-2 py-1">{{ formatDate(u.lastSeen) }}</td>
                <td class="px-2 py-1">
                  <span v-if="u.isActive === false || u.active === false || u.disabled === true" class="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-rose-100 text-rose-700">Desactivado</span>
                  <span v-else class="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700">Activo</span>
                </td>
              </tr>
              <tr v-if="!ultimosUsuarios || ultimosUsuarios.length === 0">
                <td class="px-2 py-2 text-gray-400" colspan="4">No hay datos de usuarios recientes</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="bg-white p-3 rounded-lg shadow">
        <h3 class="text-base font-medium mb-2">Top 10 Clientes con mayor deuda</h3>
        <ul>
          <li v-for="c in topClientes" :key="c.cliente" class="flex justify-between py-1 border-b text-sm">
            <span class="truncate">{{ c.cliente }}</span>
            <span class="font-semibold text-sm">{{ formatCurrency(c.deuda) }}</span>
          </li>
          <li v-if="!topClientes || topClientes.length === 0" class="text-gray-400 py-2 text-sm">Sin datos</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import empresaService from "../../composables/api/empresaService";
import userService from "../../composables/api/userService";
import { ref, onMounted } from "vue";

export default {
  name: "OwnerDashboard",
  setup() {
  const empresasCount = ref(0);
  const usuariosCount = ref(0);
  const usuariosDesactivados = ref(0);
  const dineroPendiente = ref(0);
  const ultimoMesPendiente = ref(0);
  const topClientes = ref([]);
  const ultimosUsuarios = ref([]);
  const loading = ref(false);

    const formatCurrency = (v) => {
      return (v || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });
    };

    const formatDate = (date) => {
      if (!date) return '';
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return '';
      return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    };

    const loadMetrics = async () => {
      loading.value = true;
      try {
        const empresas = await empresaService.getAllEmpresa();
        empresasCount.value = Array.isArray(empresas) ? empresas.length : 0;

        // Usuarios
        try {
          const users = await userService.getAllUsuarios();
          if (Array.isArray(users)) {
            usuariosCount.value = users.length;
            // Detectar usuarios desactivados (flag puede ser active, isActive, disabled)
            usuariosDesactivados.value = users.filter(u => u.isActive === false || u.active === false || u.disabled === true).length;

            // Últimos usuarios conectados: preferir lastLoginTime, lastLogin, lastLoginAt, fallback updatedAt o createdAt
            const withDates = users.map(u => {
              const last = u.lastLoginTime || u.lastLogin || u.lastLoginAt || u.updatedAt || u.createdAt || 0;
              const lastSeen = last ? new Date(last).getTime() : 0;
              return { ...u, lastSeen };
            });
            withDates.sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0));
            ultimosUsuarios.value = withDates.slice(0,10);
          }
        } catch (e) {
          console.warn('No se pudieron obtener usuarios para métricas', e);
        }

        // Montos y top clientes quedan como placeholders hasta definir endpoints
        dineroPendiente.value = 0;
        ultimoMesPendiente.value = 0;
        topClientes.value = [];
      } catch (e) {
        console.error("Error cargando métricas owner", e);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      loadMetrics();
    });

    return {
      empresasCount,
      usuariosCount,
      usuariosDesactivados,
      dineroPendiente,
      ultimoMesPendiente,
      topClientes,
      ultimosUsuarios,
      formatCurrency,
  formatDate,
      loadMetrics,
  loading,
    };
  },
};
</script>

<style scoped>
/* Compact table adjustments */
.compact-table th, .compact-table td {
  padding-top: 0.25rem; /* 4px */
  padding-bottom: 0.25rem; /* 4px */
}
.compact-table tbody tr { line-height: 1.1; }

/* Ensure the overflow container doesn't collapse */
.overflow-y-auto { -webkit-overflow-scrolling: touch; }
</style>
